import type { ApiResponse, ProspectForm, ProspectSubmitPayload } from "@/types";

function getApiUrl(path: string): string {
  const base = process.env.NEXT_PUBLIC_API_URL;
  if (!base) {
    throw new Error(
      "NEXT_PUBLIC_API_URL is not set. Add it to your environment configuration."
    );
  }
  return `${base.replace(/\/+$/, "")}${path}`;
}

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

function collectErrorMessages(value: unknown): string[] {
  if (typeof value === "string") return value.trim() ? [value.trim()] : [];
  if (Array.isArray(value)) return value.flatMap(collectErrorMessages);
  if (value && typeof value === "object") {
    if ("message" in value) return collectErrorMessages(value.message);
    return Object.values(value).flatMap(collectErrorMessages);
  }
  return [];
}

function createApiError(body: unknown, status: number): ApiError {
  const messages =
    body && typeof body === "object"
      ? ["message", "errors", "error"].flatMap((key) =>
          collectErrorMessages((body as Record<string, unknown>)[key])
        )
      : [];

  return new ApiError(
    [...new Set(messages)].join("\n") ||
      "Something went wrong. Please try again.",
    status
  );
}

async function readResponseBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export async function getProspectForm(
  slug: string
): Promise<ProspectForm | null> {
  const response = await fetch(
    getApiUrl(`/prospect/form/${encodeURIComponent(slug)}`),
    { cache: "no-store" }
  );

  if (response.status === 404) return null;
  if (!response.ok) {
    throw createApiError(await readResponseBody(response), response.status);
  }

  const body = (await response.json()) as ApiResponse<ProspectForm>;
  return body.data;
}

export async function submitProspectForm(
  slug: string,
  payload: ProspectSubmitPayload
): Promise<void> {
  const response = await fetch(
    getApiUrl(`/prospect/form/${encodeURIComponent(slug)}`),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const body = await readResponseBody(response);
  if (
    !response.ok ||
    (body &&
      typeof body === "object" &&
      "success" in body &&
      body.success === false)
  ) {
    throw createApiError(body, response.status);
  }
}
